<script setup lang="ts">
/**
 * Меню текущего пользователя в правом углу шапки: кто вошёл, быстрые ссылки
 * в свой профиль и свои сессии, выход.
 *
 * Выход живёт **только здесь**. В «Активных сессиях» у текущей строки кнопки
 * отзыва нет намеренно (§2.7 плана): «погасить свою же сессию» из списка —
 * ловушка, человек не ожидает, что его выкинет.
 */
import { LogOut, MonitorSmartphone, UserRound } from '@lucide/vue'
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import RoleBadge from '@/components/common/RoleBadge.vue'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/composables/useAuth'

const router = useRouter()
const { displayName, initials, role, user, store } = useAuth()

const loggingOut = ref(false)

/**
 * Уходим на `/login` **в любом случае** — отсюда и `finally`.
 *
 * `logout()` бьётся в сервер, чтобы погасить refresh-сессию в БД, и этот
 * запрос может не дойти: сеть, спящий ноутбук, остановленный бэкенд. Оставить
 * человека внутри панели после того, как он нажал «Выйти», нельзя: локальные
 * токены стор уже вычистил, следующий же запрос всё равно упадёт в 401,
 * а выглядеть это будет как сломанная кнопка.
 */
async function onLogout(): Promise<void> {
  loggingOut.value = true
  try {
    await store.logout()
  }
  catch {
    // Сервер не подтвердил выход — локально мы всё равно вышли.
  }
  finally {
    loggingOut.value = false
    await router.push({ name: 'login' })
  }
}
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <Button variant="ghost" size="icon" class="rounded-full" aria-label="Меню пользователя">
        <Avatar>
          <AvatarFallback>{{ initials }}</AvatarFallback>
        </Avatar>
      </Button>
    </DropdownMenuTrigger>

    <!--
      `w-56` перебивает дефолт реестра `w-(--reka-dropdown-menu-trigger-width)`:
      триггер здесь — круглая аватарка, и меню шириной с неё было бы нечитаемым.
    -->
    <DropdownMenuContent align="end" class="w-56">
      <DropdownMenuLabel class="flex flex-col gap-1.5">
        <span class="truncate text-sm font-medium text-foreground">{{ displayName }}</span>
        <span v-if="user" class="truncate text-xs font-normal">{{ user.email }}</span>
        <RoleBadge v-if="role" :role="role" class="w-fit" />
      </DropdownMenuLabel>

      <DropdownMenuSeparator />

      <DropdownMenuItem as-child>
        <RouterLink :to="{ name: 'profile' }">
          <UserRound />
          <span>Мой профиль</span>
        </RouterLink>
      </DropdownMenuItem>
      <DropdownMenuItem as-child>
        <RouterLink :to="{ name: 'sessions' }">
          <MonitorSmartphone />
          <span>Активные сессии</span>
        </RouterLink>
      </DropdownMenuItem>

      <DropdownMenuSeparator />

      <DropdownMenuItem variant="destructive" :disabled="loggingOut" @select="onLogout">
        <LogOut />
        <span>Выйти</span>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
